import { getDevice } from "./ua-device-parser";

class DeviceDetector {
  private localDeviceType: "tablet" | "mobile" | "desktop" = "desktop";
  private serverDeviceType: "desktop" | "ios" | "android" | "h5" = "desktop";

  private isLocalReady: boolean = false;
  private _isMobile: boolean = false;
  private _isAndroid: boolean = false;
  private _isIOS: boolean = false;

  private setLocalDeviceType() {
    if (this.isLocalReady) {
      return;
    }
    this.isLocalReady = true;
    const device = getDevice();
    if (device.type === "mobile") {
      this.localDeviceType = "mobile";
    } else if (device.type === "tablet") {
      this.localDeviceType = "tablet";
    } else {
      this.localDeviceType = "desktop";
    }
  }

  getLocalDeviceType() {
    if (this._isMobile) {
      return "mobile";
    }
    if (this.isServer()) {
      return this.serverDeviceType;
    }
    this.setLocalDeviceType();
    return this.localDeviceType;
  }

  isLocalH5() {
    return (
      this.getLocalDeviceType() === "mobile" ||
      this.getLocalDeviceType() === "tablet"
    );
  }

  isLocalPC() {
    // 如果window不存在，则认为是PC
    return (
      this.getLocalDeviceType() === "desktop" || typeof window === "undefined"
    );
  }

  /**
   * 检测是否为移动端（包括 H5 和 App）
   * @returns {boolean} 如果是移动端返回 true，否则返回 false
   */
  isMobile(): boolean {
    return this._isMobile;
  }

  /**
   * 检测是否为移动端 H5（非 App）
   * @returns {boolean} 如果是移动端 H5 返回 true，否则返回 false
   */
  isH5(): boolean {
    return this._isMobile && !this.isApp();
  }

  /**
   * 检测是否为移动端 App（Android 或 iOS）
   * 包括通过 UA/WebView 检测和 cookie 标记检测
   * @returns {boolean} 如果是移动端 App 返回 true，否则返回 false
   */
  isApp(): boolean {
    // 优先检查 cookie 标记（最可靠的标志）
    if (this.isAppFromCookie()) {
      return true;
    }
    // 通过 UA 和 WebView 标志检测
    return this.isAndroidApp() || this.isIOSApp();
  }

  /**
   * 检查 cookie 中是否标记为原生应用
   * @returns {boolean} 如果 x-is-native-app=1 则返回 true
   */
  private isAppFromCookie(): boolean {
    if (typeof document === "undefined") {
      return false;
    }

    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split("=");
      if (name === "x-is-native-app" && value === "1") {
        return true;
      }
    }

    return false;
  }

  /**
   * 检测是否为 Android App
   * 合并了 isAndroidStandaloneWebView 的检测逻辑
   * 注意：这与 isAndroidDevice() 不同，isAndroidDevice() 检测 Android 设备（包括 H5 和 App）
   * @returns {boolean} 如果是 Android App 返回 true，否则返回 false
   */
  isAndroidApp(): boolean {
    // 如果已通过 setServerDeviceType 设置，直接返回
    if (this._isAndroid) {
      return true;
    }

    if (typeof window === "undefined") {
      return false;
    }

    const deviceTypeResult = getDevice();
    const isAndroid =
      !!deviceTypeResult.appName && deviceTypeResult.os === "android";
    this._isAndroid = isAndroid;
    return isAndroid;
  }

  /**
   * 检测是否为 iOS App
   * 合并了 isIOSStandaloneWebView 的检测逻辑
   * 注意：这与 isIOSDevice() 不同，isIOSDevice() 检测 iOS 设备（包括 H5 和 App）
   * @returns {boolean} 如果是 iOS App 返回 true，否则返回 false
   */
  isIOSApp(): boolean {
    // 如果已通过 setServerDeviceType 设置，直接返回
    if (this._isIOS) {
      return true;
    }

    if (typeof window === "undefined") {
      return false;
    }

    // 优先检测 WKWebView 消息处理器（最准确的 iOS App 标志）
    // 现代 iPadOS 的 UA 可能伪装成 Mac，所以优先检测 messageHandlers
    const hasMessageHandlers = !!(window as any).webkit?.messageHandlers
      ?.bridge;

    // 如果存在 messageHandlers，直接认为是 iOS App（最可靠的标志）
    if (hasMessageHandlers) {
      return true;
    }

    const deviceTypeResult = getDevice();
    const isIOS = !!deviceTypeResult.appName && deviceTypeResult.os === "ios";
    this._isIOS = isIOS;
    return isIOS;
  }

  /**
   * @deprecated 使用 isMobile() 替代
   */
  isWebViewApp(): boolean {
    return this.isMobile();
  }

  isServer() {
    return typeof window === "undefined";
  }

  /**
   * 设置服务端设备类型和移动端信息
   * undefined 兼容 dynamic= "force-static" 没有 headers 的情况
   * @param deviceType 设备类型
   * @param isMobile 是否为移动端
   * @param platform 平台类型（'android' | 'ios' | undefined）
   * @see https://nextjs.org/docs/15/app/api-reference/file-conventions/route-segment-config#dynamic
   */
  setServerDeviceType(
    deviceType: "desktop" | "ios" | "android" | "h5" | undefined,
    isMobile: boolean = false,
    platform?: "android" | "ios" | "h5",
  ) {
    if (this.isServer()) {
      if (deviceType === undefined) {
        this.serverDeviceType = "desktop";
      } else {
        this.serverDeviceType = deviceType;
      }
    }
    this._isMobile = isMobile;
    if (isMobile && typeof window !== "undefined") {
      const deviceTypeResult = getDevice();
      if (deviceTypeResult.type === "mobile") {
        this._isAndroid =
          !!deviceTypeResult.appName && deviceTypeResult.os === "android";
        this._isIOS =
          !!deviceTypeResult.appName && deviceTypeResult.os === "ios";
      }
    } else {
      this._isAndroid = platform === "android";
      this._isIOS = platform === "ios";
    }
  }

  /**
   * @deprecated 使用 setServerDeviceType(deviceType, isMobile, platform) 替代
   */
  setServerDeviceTypeLegacy(
    deviceType: "desktop" | "ios" | "android" | "h5" | undefined,
    isWebViewApp: boolean = false,
  ) {
    this.setServerDeviceType(deviceType, isWebViewApp);
  }

  /**
   * 检查是否在浏览器环境中运行
   * @returns {boolean} 如果在浏览器中返回 true，否则返回 false
   */
  isBrowser(): boolean {
    return typeof window !== "undefined";
  }

  /**
   * 检测是否为 iOS 设备（包括 H5 和 App）
   * 注意：这与 isIOSApp() 不同，isIOSApp() 只检测 iOS App
   * @returns {boolean} 如果是 iOS 设备返回 true，否则返回 false
   */
  isIOSDevice(): boolean {
    if (!this.isBrowser()) {
      return false;
    }
    const userAgent =
      navigator.userAgent || navigator.vendor || (window as any).opera || "";
    return /iPhone|iPad|iPod/i.test(userAgent) && !(window as any).MSStream;
  }

  /**
   * 检测是否为 Android 设备（包括 H5 和 App）
   * 注意：这与 isAndroidApp() 不同，isAndroidApp() 只检测 Android App
   * @returns {boolean} 如果是 Android 设备返回 true，否则返回 false
   */
  isAndroidDevice(): boolean {
    if (!this.isBrowser()) {
      return false;
    }
    const userAgent =
      navigator.userAgent || navigator.vendor || (window as any).opera || "";
    return /Android/i.test(userAgent);
  }

  /**
   * 检测是否为 PWA 环境
   * @returns {boolean} 如果是 PWA 返回 true，否则返回 false
   */
  checkIfPWA(): boolean {
    if (!this.isBrowser()) {
      return false;
    }
    const isStandalone = window.matchMedia(
      "(display-mode: standalone)",
    ).matches;
    const isIosStandalone =
      "standalone" in window.navigator &&
      (window.navigator as any).standalone === true;
    return isStandalone || isIosStandalone;
  }

  /**
   * 检测是否为桌面设备
   * @returns {boolean} 如果是桌面设备返回 true，否则返回 false
   */
  checkIfDesktop(): boolean {
    return this.isBrowser() && !this.isMobile();
  }

  /**
   * 检测是否支持触摸事件
   * @returns {boolean} 如果支持触摸返回 true，否则返回 false
   */
  checkIfTouchDevice(): boolean {
    if (!this.isBrowser()) {
      return false;
    }
    return (
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0 ||
      (navigator as any).msMaxTouchPoints > 0
    );
  }

  /**
   * 检测是否为 Safari 浏览器
   * @returns {boolean} 如果是 Safari 返回 true，否则返回 false
   */
  checkIfSafari(): boolean {
    if (!this.isBrowser()) {
      return false;
    }
    const userAgent = navigator.userAgent.toLowerCase();
    return (
      userAgent.indexOf("safari") !== -1 && userAgent.indexOf("chrome") === -1
    );
  }

  /**
   * 检测是否为 Chrome 浏览器
   * @returns {boolean} 如果是 Chrome 返回 true，否则返回 false
   */
  checkIfChrome(): boolean {
    if (!this.isBrowser()) {
      return false;
    }
    const userAgent = navigator.userAgent.toLowerCase();
    return (
      userAgent.indexOf("chrome") !== -1 && userAgent.indexOf("edge") === -1
    );
  }

  /**
   * 检测是否为 Firefox 浏览器
   * @returns {boolean} 如果是 Firefox 返回 true，否则返回 false
   */
  checkIfFirefox(): boolean {
    if (!this.isBrowser()) {
      return false;
    }
    return navigator.userAgent.toLowerCase().indexOf("firefox") !== -1;
  }

  /**
   * 检查是否在 Chrome 扩展的 iframe 中
   * @returns {boolean} 如果在 iframe 中返回 true，否则返回 false
   */
  checkIfInChromeExtensionIframe(): boolean {
    if (!this.isBrowser()) {
      return false;
    }
    // 检查是否在 iframe 中
    const isInIframe = window.parent && window.self !== window.parent;
    return isInIframe;
  }

  /**
   * 基于 User-Agent 检测设备类型（用于客户端动态检测）
   * 返回简化的 "mobile" 或 "desktop" 类型
   * @returns {"mobile" | "desktop"} 设备类型
   */
  detectDeviceTypeFromUA(): "mobile" | "desktop" {
    if (!this.isBrowser()) {
      return "desktop";
    }

    const userAgent = navigator.userAgent;

    // 移动设备检测
    const mobileRegex =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i;
    const isMobile = mobileRegex.test(userAgent);

    if (isMobile) {
      return "mobile";
    }

    // 桌面设备检测
    const desktopRegex =
      /Windows|Macintosh|Mac OS X|Linux|Ubuntu|Debian|Fedora|CentOS|Chrome|Firefox|Safari|Edge/i;
    const isDesktop = desktopRegex.test(userAgent);

    if (isDesktop) {
      return "desktop";
    }

    // 默认返回桌面设备
    return "desktop";
  }

  /**
   * 获取当前设备信息
   * @returns {object} 设备信息对象
   */
  getDeviceInfo() {
    if (!this.isBrowser()) {
      return { isServer: true };
    }
    return {
      isMobile: this.isMobile(),
      isPWA: this.checkIfPWA(),
      isIOS: this.isIOSDevice(),
      isAndroid: this.isAndroidDevice(),
      isDesktop: this.checkIfDesktop(),
      isTouchDevice: this.checkIfTouchDevice(),
      isSafari: this.checkIfSafari(),
      isChrome: this.checkIfChrome(),
      isFirefox: this.checkIfFirefox(),
    };
  }

  /**
   * 检查 cookie 中是否标记为 iOS Store 版本
   * @returns {boolean} 如果 x-is-ios-store=1 则返回 true
   */
  isIOSStoreFromCookie(): boolean {
    if (typeof document === "undefined") {
      return false;
    }

    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split("=");
      if (name === "x-is-ios-store" && value === "1") {
        return true;
      }
    }

    return false;
  }
}

export const deviceDetector = new DeviceDetector();

export const getDeviceType = () => {
  if (deviceDetector.isAndroidApp()) {
    return "android";
  }
  if (deviceDetector.isIOSApp()) {
    return "ios";
  }
  return "pc";
};

export const getTypeAppOrPc = () => {
  const deviceType = getDeviceType();
  if (deviceType === "android" || deviceType === "ios") {
    return "app";
  }
  return "pc";
};
